import { useSuspenseQuery } from '@apollo/client/react';
import { ErrorAlert } from '@optools/components/alerts/error';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@optools/components/ui/tabs';
import { dateSchema } from '@optools/schemas/date';
import { gamePkSchema } from '@optools/schemas/game';
import { GET_MILESTONES_GAME } from '@optools/services/operator-gateway/milestones/queries';
import { MilestonesTab } from '@optools/tools/milestones/components/milestones-tab';
import { MilestoneEditorContextProvider } from '@optools/tools/milestones/context/milestone-editor-context';
import { filterVideoContent } from '@optools/tools/milestones/utils/content';
import { sortByFeedType } from '@optools/utils/feeds';
import { createFileRoute, useParams, useSearch } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useState } from 'react';

export const Route = createFileRoute('/_auth/milestones/$gamePk')({
  component: RouteComponent,
  params: { parse: zodValidator(gamePkSchema).parse },
  validateSearch: zodValidator(dateSchema),
});

export function RouteComponent() {
  const { gamePk } = useParams({ from: '/_auth/milestones/$gamePk' });
  const { date } = useSearch({ from: '/_auth/milestones/$gamePk' });
  const [selectedTab, setSelectedTab] = useState<string | null>(null);

  const { data } = useSuspenseQuery(GET_MILESTONES_GAME, {
    variables: { gamePk, scheduleDate: date },
  });

  const game = data?.game;

  if (!game) return null;

  const content = sortByFeedType(filterVideoContent(game.content));

  if (!content.length) {
    return (
      <ErrorAlert
        description="There is no video content available for this game."
        title="No Video Content"
      />
    );
  }
  const currentTab = selectedTab ?? content[0]?.mediaId;

  return (
    <div className="h-full">
      <Tabs
        className="flex h-full flex-col items-start"
        value={currentTab ?? content[0]?.mediaId}
        onValueChange={setSelectedTab}>
        <TabsList className="mb-4">
          {content.map(({ callSign, feedType, mediaId }) => (
            <TabsTrigger key={mediaId} value={mediaId}>
              {`${feedType}${callSign ? ` - ${callSign}` : ''}`}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent className="w-full" value={currentTab}>
          <MilestoneEditorContextProvider mediaId={currentTab} pipelines={game.pipelines}>
            <MilestonesTab mediaId={currentTab} />
          </MilestoneEditorContextProvider>
        </TabsContent>
      </Tabs>
    </div>
  );
}
